/**
 * axisPointer 组件扩展
 *
 * @see https://echarts.apache.org/zh/option.html#axisPointer
 */
import type GlobalModel from 'echarts/types/src/model/Global';
import type GridModel from 'echarts/types/src/coord/cartesian/GridModel';
import { type EChartsExtensionInstallRegisters } from 'echarts/types/src/extension';
import AxisPointerView from 'echarts/lib/component/axisPointer/AxisPointerView.js';
import * as globalListener from 'echarts/lib/component/axisPointer/globalListener.js';
import { getAxisPointerModel } from 'echarts/lib/component/axisPointer/modelHelper.js';

AxisPointerView.prototype.render = function (globalAxisPointerModel, ecModel, api) {
  const globalTooltipModel = ecModel.getComponent('tooltip');
  const triggerOn =
    globalAxisPointerModel.get('triggerOn') ||
    (globalTooltipModel && globalTooltipModel.get('triggerOn')) ||
    'mousemove|click'; // Register global listener in AxisPointerView to enable
  // AxisPointerView to be independent to Tooltip.

  globalListener.register('axisPointer', api, function (currTrigger, e, dispatchAction) {
    // If 'none', it is not controlled by mouse totally.
    if (triggerOn !== 'none' && (currTrigger === 'leave' || triggerOn.indexOf(currTrigger) >= 0)) {
      const touchPoint = [e && e.offsetX, e && e.offsetY];
      let isTouchPointInGrid = false;
      let nearestGridModel;
      const axisPointerModel = api?.getModel().getComponent('axisPointer');
      const dvShowOutOfGrid = axisPointerModel?.option.dvShowOutOfGrid;
      const dvHideWhenClickInGrid =
        axisPointerModel?.option.dvHideWhenClickInGrid && currTrigger === 'click';
      // 若设置了dvShowOutOfGrid或dvHideWhenClickInGrid为true且未点在grid内部时，则通过遍历gridModel找到离点击位置最近的gridModel
      if (dvShowOutOfGrid || dvHideWhenClickInGrid) {
        // 如果touchPoint不在grid内，则要找到离touchPoint最近的grid
        api.getModel().eachComponent('grid', compModel => {
          const rect = compModel.coordinateSystem.getRect();
          // ! 在 grid 内
          if (rect.contain(...touchPoint) || isTouchPointInGrid) {
            isTouchPointInGrid = true;
            nearestGridModel = undefined;
            return;
          }
          if (!nearestGridModel) {
            // 把当前grid当成“最近的grid”
            nearestGridModel = compModel;
            return;
          }
          const rectCenter = [rect.x + rect.width / 2, rect.y + rect.height / 2];
          const lastRect = nearestGridModel.coordinateSystem.getRect();
          const lastRectCenter = [
            lastRect.x + lastRect.width / 2,
            lastRect.y + lastRect.height / 2
          ];
          if (
            (touchPoint[0] - lastRectCenter[0]) ** 2 + (touchPoint[1] - lastRectCenter[1]) ** 2 >
            (touchPoint[0] - rectCenter[0]) ** 2 + (touchPoint[1] - rectCenter[1]) ** 2
          ) {
            // 若当前grid离touchPoint更近，则把当前grid作为“最近的grid”
            nearestGridModel = compModel;
          }
        });
      }
      if (dvShowOutOfGrid) {
        if (api.dvMouseThroughGrid && nearestGridModel) {
          const rect = nearestGridModel.coordinateSystem.getRect();
          const rectCenter = [rect.x + rect.width / 2, rect.y + rect.height / 2];
          // 设置dvShowOutOfGrid为true，则需要在手指超出grid的y轴范围且未超出其x轴范围时仍然显示axisPointer
          // 所以需要将触摸点的y坐标改为合法值，使用grid的中心的y坐标作为合法值
          if (touchPoint[0] >= rect.x && touchPoint[0] <= rect.x + rect.width) {
            touchPoint[1] = rectCenter[1];
          }
        }
      }
      if (dvHideWhenClickInGrid && isTouchPointInGrid) {
        const xAxisModel = ecModel.getComponent('xAxis');
        const yAxisModel = ecModel.getComponent('yAxis');
        const xAxisPointerModel = getAxisPointerModel(xAxisModel);
        const yAxisPointerModel = getAxisPointerModel(yAxisModel);
        // 设置dvHideWhenClickInGrid为true，需要在点击grid内部且已经有axisPointer显示时，隐藏axisPointer，若无axisPointer显示则正常显示axisPointer
        // 先判断是否已经有axisPointer显示。若有，将触摸点设为非法值，此处将x坐标设为null
        if (
          xAxisPointerModel?.get('status') === 'show' ||
          yAxisPointerModel?.get('status') === 'show'
        ) {
          touchPoint[0] = null;
        }
      }

      dispatchAction({
        type: 'updateAxisPointer',
        currTrigger,
        x: touchPoint[0],
        y: touchPoint[1]
      });
    }
  });
};

export function install(registers: EChartsExtensionInstallRegisters) {
  // eslint-disable-next-line sonarjs/cognitive-complexity
  registers.registerUpdateLifecycle('afterinit', echartsIns => {
    /**
     * * 没有渲染任何数据内容时，不显示 axisPointer
     *
     * ! 当前的实现仅考虑了利用配置项进行控制的简单场景，实际上还应该判断 axisPointer 与哪几个系列相关联
     */
    echartsIns.on('updateAxisPointer', event => {
      const axisPointerOptions: { dvShowOnlyWhenRenderingContent?: boolean }[] =
        echartsIns.getOption().axisPointer || [];

      if (
        event.axesInfo.length <= 0 ||
        !axisPointerOptions.some(opt => opt.dvShowOnlyWhenRenderingContent === true)
      ) {
        return;
      }

      const allSeriesNotAlive = (echartsIns._chartsViews as { __alive: boolean }[]).every(
        seriesView => seriesView.__alive === false
      );

      if (allSeriesNotAlive) {
        // 隐藏
        echartsIns.dispatchAction({ type: 'updateAxisPointer' });
      }

      //
    });

    /**
     * * 表内触发 axisPointer 后，手指移出图表外依然触发 axisPointer
     */
    echartsIns.getZr().on('mousedown', event => {
      (echartsIns.getModel() as GlobalModel)?.eachComponent('grid', (compModel: GridModel) => {
        const rect = compModel.coordinateSystem.getRect();
        echartsIns._api.dvMouseThroughGrid = rect.contain(event.offsetX, event.offsetY);
      });
    });

    echartsIns.getZr().on('mouseup', () => {
      echartsIns._api.dvMouseThroughGrid = false;
    });

    echartsIns.getZr().on('mousemove', event => {
      if (event.$dvGetInteractionType() !== '') {
        return;
      }

      (echartsIns.getModel() as GlobalModel)?.eachComponent('grid', (compModel: GridModel) => {
        const rect = compModel.coordinateSystem.getRect();
        if (rect.contain(event.offsetX, event.offsetY)) {
          echartsIns._api.dvMouseThroughGrid = true;
        }
      });
    });
  });
}
